/** ReSTIR GI の余弦半球サンプルと Lambert 打ち消し。シェーダと同じ式。 */

export const GI_FIREFLY_LUMINANCE = 8;
export const GI_MAX_HISTORY_M = 64;
/** ReSTIR GI の拡散バウンスの倍率。 */
export const GI_DIFFUSE_GAIN = 1;
export const GI_DIFFUSE_GAIN_MIN = 0;
export const GI_DIFFUSE_GAIN_MAX = 16;
/** この距離まで GI をフルで載せる（メートル = ブロック）。 */
export const GI_DISTANCE_FADE_START = 8;
/** この距離で GI を切る。以降はリザーバも作らない。 */
export const GI_DISTANCE_FADE_END = 20;
/** 8〜20m で載せる固定環境光の強さ。1 が開けた面の GI 空ミスと同じ。 */
export const GI_DISTANCE_AMBIENT = 0.5;
export const GI_DISTANCE_AMBIENT_MIN = 0;
export const GI_DISTANCE_AMBIENT_MAX = 2;
/** 8m 以内の固定環境光。 */
export const GI_NEAR_AMBIENT = 0.05;
/** 環境光を白に寄せる量。1 が真っ白、0 が元の空／地面色。 */
export const GI_SKY_WHITE_MIX = 0.4;
export const GI_NEAR_AMBIENT_MIN = 0;
export const GI_NEAR_AMBIENT_MAX = 1;
export const PI = Math.PI;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

export function clampGiSlider(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

/** localStorage 未設定や空文字は 0 にせず、もともとの定数へ戻す。 */
export function readGiSlider(raw: string | null, min: number, max: number, fallback: number): number {
  if (raw === null || raw === '') return fallback;
  return clampGiSlider(Number(raw), min, max, fallback);
}

export function vec3(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function length(v: Vec3): number {
  return Math.hypot(v.x, v.y, v.z);
}

export function normalize(v: Vec3): Vec3 {
  const n = length(v);
  if (n < 1e-8) return vec3(0, 1, 0);
  return vec3(v.x / n, v.y / n, v.z / n);
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return vec3(a.x + b.x, a.y + b.y, a.z + b.z);
}

export function scale(v: Vec3, s: number): Vec3 {
  return vec3(v.x * s, v.y * s, v.z * s);
}

/** Duff の接空間。n は単位ベクトル。 */
export function tangentFrame(n: Vec3): { t: Vec3; b: Vec3 } {
  if (n.z < 0) {
    const a = 1 / (1 - n.z);
    const b = n.x * n.y * a;
    return {
      t: vec3(1 - n.x * n.x * a, -b, n.x),
      b: vec3(b, n.y * n.y * a - 1, -n.y),
    };
  }
  const a = 1 / (1 + n.z);
  const b = -n.x * n.y * a;
  return {
    t: vec3(1 - n.x * n.x * a, b, -n.x),
    b: vec3(b, 1 - n.y * n.y * a, -n.y),
  };
}

export function localToWorld(local: Vec3, n: Vec3): Vec3 {
  const { t, b } = tangentFrame(n);
  return normalize(
    vec3(
      t.x * local.x + b.x * local.y + n.x * local.z,
      t.y * local.x + b.y * local.y + n.y * local.z,
      t.z * local.x + b.z * local.y + n.z * local.z
    )
  );
}

/** z-up 接空間の余弦重みサンプル。u1, u2 は [0, 1)。 */
export function cosineHemisphereLocal(u1: number, u2: number): Vec3 {
  const r = Math.sqrt(clamp01(u1));
  const phi = 2 * PI * u2;
  const z = Math.sqrt(clamp01(1 - u1));
  return vec3(r * Math.cos(phi), r * Math.sin(phi), z);
}

export function cosineHemisphereWorld(n: Vec3, u1: number, u2: number): Vec3 {
  return localToWorld(cosineHemisphereLocal(u1, u2), normalize(n));
}

export function cosineHemispherePdf(nDotL: number): number {
  return Math.max(nDotL, 0) / PI;
}

/**
 * 余弦サンプル 1 本の拡散 irradiance（albedo を掛けない）。
 * 合成側で `E * albedo * (1 - metalness) / π` する。
 */
export function cosineIrradiance(lo: Vec3): Vec3 {
  return scale(lo, PI);
}

export function clampLuminance(color: Vec3, maxLum: number): Vec3 {
  const lum = 0.2126 * color.x + 0.7152 * color.y + 0.0722 * color.z;
  if (lum <= maxLum || lum < 1e-8) return color;
  return scale(color, maxLum / lum);
}

export function octEncode(n: Vec3): { x: number; y: number } {
  const v = normalize(n);
  const inv = 1 / (Math.abs(v.x) + Math.abs(v.y) + Math.abs(v.z));
  let x = v.x * inv;
  let y = v.y * inv;
  if (v.z < 0) {
    const ox = x;
    x = (1 - Math.abs(y)) * Math.sign(ox || 1);
    y = (1 - Math.abs(ox)) * Math.sign(y || 1);
  }
  return { x, y };
}

export function octDecode(x: number, y: number): Vec3 {
  const z = 1 - Math.abs(x) - Math.abs(y);
  const n = z >= 0 ? vec3(x, y, z) : vec3((1 - Math.abs(y)) * Math.sign(x || 1), (1 - Math.abs(x)) * Math.sign(y || 1), z);
  return normalize(n);
}

export function diffuseGiOutgoing(albedo: Vec3, metalness: number, irradiance: Vec3): Vec3 {
  const k = (1 - clamp01(metalness)) / PI;
  return vec3(albedo.x * irradiance.x * k, albedo.y * irradiance.y * k, albedo.z * irradiance.z * k);
}
